// The "⋯" menu on a file, and the panel it can turn a row into. The popover is
// `RowEdit.tsx`'s `RowMenu`, which is `AccountMenu`'s mechanism (anchored, click-away and
// Escape close it) shared with the family page.

import { useState } from 'react';

import type { ApiClient } from '../api/client.js';
import type { FileView, Person } from '../api/contract.js';
import { fileProblem, VisibilityPicker, type Visibility } from './AddFile.js';
import { RowMenu } from './RowEdit.js';

export type FileMode = 'view' | 'change' | 'delete';

export function FileMenu({
  client,
  planId,
  file,
  onMode,
  onProblem,
}: {
  client: ApiClient;
  planId: string;
  file: FileView;
  onMode: (mode: FileMode) => void;
  onProblem: (message: string | null) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const items = [
    {
      label: 'Download',
      run: () => {
        onProblem(null);
        client.downloadFileOriginal(planId, file.id, file.name).catch(() => {
          onProblem('That didn’t download. Try again.');
        });
      },
    },
    ...(file.canChange
      ? [
          { label: 'Change who can see it', run: () => onMode('change') },
          { label: 'Delete', run: () => onMode('delete') },
        ]
      : []),
  ];
  return (
    <RowMenu
      open={open}
      onToggle={() => setOpen((value) => !value)}
      onPick={() => setOpen(false)}
      items={items}
      name={file.name}
    />
  );
}

function currentChoice(file: FileView): Visibility {
  return file.visibility.kind === 'person'
    ? 'just_me'
    : file.visibility.kind === 'principals'
      ? 'principals'
      : 'everyone';
}

/** What a row shows in place of itself while "Change who can see it" is chosen. */
export function FileEditPanel({
  client,
  planId,
  file,
  people,
  viewerIsPrincipal,
  onCancel,
  onDone,
}: {
  client: ApiClient;
  planId: string;
  file: FileView;
  people: readonly Person[];
  viewerIsPrincipal: boolean;
  onCancel: () => void;
  onDone: () => void;
}): JSX.Element {
  const [choice, setChoice] = useState<Visibility>(currentChoice(file));
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const run = async (): Promise<void> => {
    setBusy(true);
    setProblem(null);
    try {
      await client.setFileVisibility(planId, file.id, choice);
      onDone();
    } catch (error) {
      setProblem(fileProblem(error));
      setBusy(false);
    }
  };

  const onKeyDown = (event: React.KeyboardEvent): void => {
    if (event.key === 'Escape' && !busy) {
      event.preventDefault();
      onCancel();
    }
  };

  return (
    <form
      className="fileEdit"
      onKeyDown={onKeyDown}
      onSubmit={(event) => {
        event.preventDefault();
        void run();
      }}
    >
      <VisibilityPicker
        people={people}
        viewerIsPrincipal={viewerIsPrincipal}
        value={choice}
        onChange={setChoice}
      />
      {problem === null ? null : (
        <p className="artifact__problem" role="alert">
          {problem}
        </p>
      )}
      <div className="fileForm__buttons">
        <button type="button" className="button" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
        <button
          type="submit"
          className="button button--primary"
          disabled={busy || choice === currentChoice(file)}
        >
          Save
        </button>
      </div>
    </form>
  );
}
